import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Pagination from '../components/common/Pagination';
import api from '../api/axiosConfig';
import ImprovedClaimModal from '../components/ImprovedClaimModal';
import * as ExcelJS from '../utils/exceljsCompat';
// 처리유형 옵션 (이미지와 동일하게)
const CONSULT_TYPE_OPTIONS = [
    { label: "지결", type: "출고" },
    { label: "누락 재발송", type: "출고" },
    { label: "변심 맞교환", type: "출고" },
    { label: "분실 재발송", type: "출고" },
    { label: "불량 맞교환", type: "출고" },
    { label: "불량 재발송", type: "출고" },
    { label: "신규", type: "출고" },
    { label: "오배송 맞교환", type: "출고" },
    { label: "오배송 재발송", type: "출고" },
    { label: "직거래", type: "출고" },
    { label: "파손 맞교환", type: "출고" },
    { label: "파손 재발송", type: "출고" },
    { label: "회수지연 재회수", type: "출고" },
    { label: "불량 반품회수", type: "반품" },
    { label: "변심 반품회수", type: "반품" },
    { label: "오배송 반품회수", type: "반품" },
    { label: "파손 반품회수", type: "반품" },
    { label: "배송지연 반품회수", type: "반품" },
];
// 택배사 옵션 (경동, 천일, 한진 포함)
const courierOptions = ['경동', '천일', '한진', 'CJ대한통운', '우체국', '로젠', '현장', '직배송', '기타'];
function getCheonilDeliveryCode(delvcost) {
    const codeMap = {
        '4500': 'KGB80003037',
        '5000': 'KGB80003038',
        '5500': 'KGB80003039',
        '6000': 'KGB80003040',
        '6500': 'KGB80003041',
        '7000': 'KGB80003042',
        '7500': 'KGB80003043',
        '8000': 'KGB80003044',
        '8500': 'KGB80003045',
        '9000': 'KGB80003046',
        '9500': 'KGB80003047',
        '10000': 'KGB80003048',
        '10500': 'KGB80003049',
        '11000': 'KGB80003050',
        '12000': 'KGB80003051',
        '13000': 'KGB80003052',
        '14000': 'KGB80003053',
        '15000': 'KGB80003054',
        '16000': 'KGB80003055',
        '17000': 'KGB80003056',
        '18000': 'KGB80003057',
        '19000': 'KGB80003058',
        '20000': 'KGB80003059',
        '25000': 'KGB80003060',
        '40000': 'KGB80003061',
        '50000': 'KGB80003062',
        '60000': 'KGB80003063',
        '70000': 'KGB80003064',
        '80000': 'KGB80003065',
        '100000': 'KGB80003066',
        '4700': 'KGB80003067',
        '5200': 'KGB80003068',
        '5700': 'KGB80003069',
    };
    return codeMap[String(Math.floor(Number(delvcost)))] || 'KGB80003040'; // 기본값(6000원)
}
function getBoxTypCdByFee(fee) {
    const map = {
        '2500': 'A',
        '3000': 'B',
        '4000': 'C',
        '5000': 'D',
    };
    return map[String(Math.floor(Number(fee)))] || 'A'; // 기본값 'A'
}
// Products 파싱 유틸리티 함수들
const parseProducts = (productsData) => {
    try {
        // 이미 배열인 경우
        if (Array.isArray(productsData)) {
            return productsData;
        }
        // 문자열인 경우 JSON 파싱
        if (typeof productsData === 'string') {
            return JSON.parse(productsData);
        }
        // null이나 undefined인 경우
        if (!productsData) {
            return [];
        }
        return [];
    }
    catch (error) {
        console.error('Products 파싱 오류:', error);
        return [];
    }
};
// 상품코드 추출 함수
const getProductCodes = (consultation) => {
    const products = parseProducts(consultation.products);
    return products.map(product => product.product_code || product.productCode || '').filter(code => code);
};
// 모든 상품코드를 콤마로 구분하여 반환
const getAllProductCodes = (consultation) => {
    const codes = getProductCodes(consultation);
    return codes.length > 0 ? codes.join(', ') : '';
};
// 상품명 추출 함수
const getProductNames = (consultation) => {
    const products = parseProducts(consultation.products);
    return products.map(product => product.product_name || product.productName || '').filter(name => name);
};
// 첫 번째 상품코드 가져오기
const getFirstProductCode = (consultation) => {
    const codes = getProductCodes(consultation);
    return codes.length > 0 ? codes[0] : '';
};
// 첫 번째 상품명 가져오기
const getFirstProductName = (consultation) => {
    const names = getProductNames(consultation);
    return names.length > 0 ? names[0] : consultation.product_name || '';
};
// 모든 상품명을 콤마로 구분하여 반환
const getAllProductNames = (consultation) => {
    const names = getProductNames(consultation);
    return names.length > 0 ? names.join(', ') : consultation.product_name || '';
};
// 상품 개수 가져오기
const getProductCount = (consultation) => {
    const products = parseProducts(consultation.products);
    return products.length || consultation.total_product_count || 0;
};
const ConsultationProcessing = () => {
    const navigate = useNavigate();
    const pageTitle = "상담처리";
    // 상태 관리
    const [consultations, setConsultations] = useState([]);
    const [filteredConsultations, setFilteredConsultations] = useState([]);
    const [selectedConsultations, setSelectedConsultations] = useState([]);
    const [showDetailSearch, setShowDetailSearch] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    // 검색 필터
    const [searchTerm, setSearchTerm] = useState('');
    const [searchColumn, setSearchColumn] = useState('recipient_name');
    const [status, setStatus] = useState('');
    // 최근 1주일 날짜 계산 유틸리티
    const getInitialDates = () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 7);
        return {
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        };
    };
    const initialDates = getInitialDates();
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);
    const [dateColumn, setDateColumn] = useState('created_at');
    // 모달 상태
    const [showAddModal, setShowAddModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [claimOrder, setClaimOrder] = useState(null);
    const [isClaimLoading, setIsClaimLoading] = useState(false);
    // 새 상담처리 정보
    const [newConsultation, setNewConsultation] = useState({
        id: 0,
        order_number: '',
        order_date: '',
        mall_id: '',
        product_name: '',
        recipient_name: '',
        contact: '',
        address: '',
        process_type: '',
        receiver: '',
        claim_content: '',
        claim_comp: '',
        bank_name: '',
        account_number: '',
        depositor: '',
        refund_amount: 0,
        return_box: 0,
        return_fee: 0,
        return_fee_type: '',
        courier_company: '',
        courier_fee: 0,
        status: '접수',
        created_at: '',
        updated_at: '',
    });
    // CS 메모 관리
    const [csMemos, setCsMemos] = useState([]);
    const [newMemo, setNewMemo] = useState({
        id: '',
        date: new Date().toISOString().split('T')[0],
        manager: '',
        content: ''
    });
    // 파일 업로드를 위한 ref
    const fileInputRef = useRef(null);
    const [uploadedConsultations, setUploadedConsultations] = useState([]);
    const [uploadError, setUploadError] = useState('');
    // 필터 상태 추가
    const [filters, setFilters] = useState({});
    // 필터 드롭다운 열린 상태
    const [openFilterColumn, setOpenFilterColumn] = useState(null);
    // 필터 토글 함수
    const toggleFilterValue = (columnKey, value) => {
        const newFilters = { ...filters };
        if (!newFilters[columnKey]) {
            newFilters[columnKey] = [value];
        }
        else if (newFilters[columnKey].includes(value)) {
            newFilters[columnKey] = newFilters[columnKey].filter(k => k !== value);
            if (newFilters[columnKey].length === 0) {
                delete newFilters[columnKey];
            }
        }
        else {
            newFilters[columnKey].push(value);
        }
        setFilters(newFilters);
    };
    // 필터 적용 함수
    const applyFilters = (data) => {
        return data.filter(item => {
            return Object.entries(filters).every(([key, values]) => {
                return values.includes(String(item[key]));
            });
        });
    };
    // 필터 드롭다운 렌더링 헬퍼
    const renderFilterHeader = (label, columnKey) => {
        const uniqueValues = Array.from(new Set(consultations.map(item => item[columnKey]))).filter(Boolean);
        const isOpen = openFilterColumn === columnKey;
        const hasFilter = filters[columnKey] && filters[columnKey].length > 0;
        return (_jsxs("div", { className: "flex items-center gap-1 relative", children: [_jsx("span", { children: label }), _jsx("button", { className: "text-xs cursor-pointer", style: { color: hasFilter ? '#2563eb' : '#9ca3af' }, onClick: (e) => {
                        e.stopPropagation();
                        setOpenFilterColumn(isOpen ? null : columnKey);
                    }, children: "\u25BC" }), isOpen && (_jsxs("div", { className: "absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 min-w-[120px] max-h-[200px] overflow-auto", children: [uniqueValues.map(value => {
                            var _a;
                            return (_jsxs("div", { className: "px-3 py-1 text-xs cursor-pointer hover:bg-gray-100 flex items-center gap-1", onClick: (e) => {
                                    e.stopPropagation();
                                    toggleFilterValue(columnKey, String(value));
                                }, children: [((_a = filters[columnKey]) === null || _a === void 0 ? void 0 : _a.includes(String(value))) && _jsx("span", { children: "\u2713" }), _jsx("span", { children: String(value) })] }, String(value)));
                        }), uniqueValues.length === 0 && (_jsx("div", { className: "px-3 py-1 text-xs text-gray-400", children: "\uB370\uC774\uD130 \uC5C6\uC74C" }))] }))] }));
    };
    // 고객 상담 데이터 로드
    const loadConsultations = async () => {
        setLoading(true);
        try {
            // 기간 필터 적용하여 요청 (서버 부하 감소 및 속도 향상)
            const response = await api.get(`/api/consultations/all`, {
                params: {
                    startDate,
                    endDate
                }
            });
            if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data.consultations)) {
                // 첫 번째 상담의 상세 정보 로깅
                if (response.data.data.consultations.length > 0) {
                    const firstConsultation = response.data.data.consultations[0];
                    if (firstConsultation.products && firstConsultation.products.length > 0) {
                    }
                }
                // 상품이 있는 상담과 없는 상담 개수 확인
                const consultationsWithProducts = response.data.data.consultations.filter((c) => c.products && Array.isArray(c.products) && c.products.length > 0);
                const consultationsWithoutProducts = response.data.data.consultations.filter((c) => !c.products || !Array.isArray(c.products) || c.products.length === 0);
                // mall_name을 mall_id로 매핑하여 처리 (테스트 데이터 제거)
                const mappedData = response.data.data.consultations.map((consultation, index) => {
                    // 데이터 유효성 검사
                    if (!consultation || typeof consultation !== 'object') {
                        console.warn(`Index ${index}: 유효하지 않은 상담 데이터`, consultation);
                        return null;
                    }
                    // 쇼핑몰 ID 생성 로직
                    let mallId = consultation.mall_name || '';
                    // mall_name이 비어있는 경우에만 임시 쇼핑몰 ID 생성
                    if (!mallId || mallId.trim() === '') {
                        mallId = '쇼핑몰정보없음';
                    }
                    else {
                    }
                    // Products 데이터 파싱
                    let parsedProducts = [];
                    try {
                        parsedProducts = parseProducts(consultation.products);
                        if (parsedProducts.length > 0) {
                        }
                    }
                    catch (error) {
                        console.error(`Index ${index}: Products 파싱 오류:`, error);
                        parsedProducts = [];
                    }
                    const result = {
                        ...consultation,
                        mall_id: mallId,
                        products: parsedProducts // 파싱된 상품 데이터로 교체
                    };
                    return result;
                }).filter((item) => item !== null); // null 값 제거
                setConsultations(mappedData);
                setFilteredConsultations(mappedData);
            }
            else {
                console.error('응답 데이터 형식이 올바르지 않습니다:', response.data);
                setConsultations([]);
                setFilteredConsultations([]);
            }
        }
        catch (error) {
            console.error('고객 상담 데이터 로드 실패:', error);
            alert('고객 상담 데이터를 불러오는데 실패했습니다.');
            setConsultations([]);
            setFilteredConsultations([]);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        loadConsultations();
    }, []);
    // 날짜 정규화 함수 수정 - 더 안전한 방식
    const normalizeDate = (dateValue) => {
        if (!dateValue)
            return null;
        try {
            // 다양한 날짜 형식을 YYYY-MM-DD로 통일
            let normalizedDate;
            let date = new Date(dateValue);
            dateValue = new Date(date.getTime() + (9 * 60 * 60 * 1000)).toISOString();
            if (dateValue.includes('T')) {
                // ISO 형식 (2024-01-01T00:00:00.000Z)
                normalizedDate = dateValue.split('T')[0];
            }
            else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // 이미 YYYY-MM-DD 형식
                normalizedDate = dateValue;
            }
            else if (dateValue.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
                // YYYY-M-D 또는 YYYY-MM-D 형식을 YYYY-MM-DD로 변환
                const parts = dateValue.split('-');
                const year = parts[0];
                const month = parts[1].padStart(2, '0');
                const day = parts[2].padStart(2, '0');
                normalizedDate = `${year}-${month}-${day}`;
            }
            else {
                // 다른 형식은 Date 객체로 변환 후 YYYY-MM-DD로 변환
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) {
                    console.warn('유효하지 않은 날짜 형식:', dateValue);
                    return null;
                }
                // UTC 기준으로 변환하여 시간대 이슈 방지
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                normalizedDate = `${year}-${month}-${day}`;
            }
            // YYYY-MM-DD 형식 검증
            if (!normalizedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                console.warn('정규화된 날짜가 올바른 형식이 아님:', normalizedDate);
                return null;
            }
            // 유효한 날짜인지 확인
            const testDate = new Date(normalizedDate + 'T00:00:00');
            if (isNaN(testDate.getTime())) {
                console.warn('정규화된 날짜가 유효하지 않음:', normalizedDate);
                return null;
            }
            return normalizedDate;
        }
        catch (error) {
            console.error('날짜 정규화 오류:', dateValue, error);
            return null;
        }
    };
    // useEffect 수정 - 필터 적용
    useEffect(() => {
        let filtered = [...consultations];
        if (searchTerm) {
            filtered = filtered.filter(consultation => {
                const value = consultation[searchColumn];
                return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
            });
        }
        if (status) {
            filtered = filtered.filter(consultation => consultation.status === status);
        }
        // 날짜 필터링 로직
        if (startDate || endDate) {
            // 필터링 조건 명시
            if (startDate && !endDate) {
            }
            else if (!startDate && endDate) {
            }
            else if (startDate && endDate) {
            }
            const beforeFilterCount = filtered.length;
            let matchedCount = 0;
            let notMatchedCount = 0;
            filtered = filtered.filter(consultation => {
                const rawDateValue = consultation[dateColumn];
                if (!rawDateValue) {
                    notMatchedCount++;
                    return false; // 날짜가 없으면 제외
                }
                const normalizedDate = normalizeDate(rawDateValue);
                if (!normalizedDate) {
                    notMatchedCount++;
                    return false;
                }
                // 날짜 비교 로직
                let includeItem = true;
                let reason = '';
                // 시작일 조건 확인 (시작일 이상)
                if (startDate) {
                    const normalizedStartDate = normalizeDate(startDate);
                    if (normalizedStartDate) {
                        const startCheck = normalizedDate >= normalizedStartDate;
                        if (!startCheck) {
                            includeItem = false;
                            reason += `시작일 미달(${normalizedDate} < ${normalizedStartDate}) `;
                        }
                        else {
                            reason += `시작일 만족(${normalizedDate} >= ${normalizedStartDate}) `;
                        }
                    }
                }
                // 종료일 조건 확인 (종료일 이하)
                if (endDate && includeItem) {
                    const normalizedEndDate = normalizeDate(endDate);
                    if (normalizedEndDate) {
                        const endCheck = normalizedDate <= normalizedEndDate;
                        if (!endCheck) {
                            includeItem = false;
                            reason += `종료일 초과(${normalizedDate} > ${normalizedEndDate}) `;
                        }
                        else {
                            reason += `종료일 만족(${normalizedDate} <= ${normalizedEndDate}) `;
                        }
                    }
                }
                // 디버깅을 위해 처음 몇 개의 결과만 로그 출력
                if (matchedCount + notMatchedCount < 10) {
                }
                if (includeItem) {
                    matchedCount++;
                }
                else {
                    notMatchedCount++;
                }
                return includeItem;
            });
            // 시작일만 선택한 경우 추가 검증
            if (startDate && !endDate) {
                if (filtered.length > 0) {
                    const dates = filtered.map(c => normalizeDate(c[dateColumn])).filter(Boolean).sort();
                }
            }
        }
        // 추가 필터 적용
        filtered = applyFilters(filtered);
        // 역순으로 정렬
        filtered.sort((a, b) => b.id - a.id);
        setFilteredConsultations(filtered);
    }, [consultations, searchTerm, searchColumn, status, startDate, endDate, dateColumn, filters]);
    // 상담 선택 처리
    const handleSelectConsultation = (e, id) => {
        if (e.target.checked) {
            setSelectedConsultations([...selectedConsultations, id]);
        }
        else {
            setSelectedConsultations(selectedConsultations.filter(selectedId => selectedId !== id));
        }
    };
    // 전체 선택/해제
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedConsultations(currentItems.map(consultation => consultation.id));
        }
        else {
            setSelectedConsultations([]);
        }
    };
    // 상담 삭제
    const handleDeleteConsultation = async (id) => {
        var _a, _b;
        if (window.confirm('선택한 상담내역을 삭제하시겠습니까?')) {
            try {
                setLoading(true);
                // API를 통해 상담 삭제
                await api.delete(`/api/consultations/${id}`);
                // 성공적으로 삭제된 후 로컬 상태 업데이트
                setConsultations(consultations.filter(consultation => consultation.id !== id));
                setSelectedConsultations(selectedConsultations.filter(consultationId => consultationId !== id));
                alert('상담내역이 성공적으로 삭제되었습니다.');
                // 데이터 다시 로드 (서버와 동기화)
                await loadConsultations();
            }
            catch (error) {
                console.error('상담 삭제 중 오류 발생:', error);
                const errorMessage = ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || error.message || '상담 삭제 중 오류가 발생했습니다.';
                alert(errorMessage);
            }
            finally {
                setLoading(false);
            }
        }
    };
    // 선택한 상담 삭제
    const handleDeleteSelected = async () => {
        var _a, _b;
        if (selectedConsultations.length === 0) {
            alert('삭제할 상담내역을 선택해주세요.');
            return;
        }
        if (window.confirm(`선택한 ${selectedConsultations.length}개의 상담내역을 삭제하시겠습니까?`)) {
            try {
                setLoading(true);
                // 선택된 상담들을 순차적으로 삭제
                for (const consultationId of selectedConsultations) {
                    await api.get(`/api/consultations/delete/${consultationId}`);
                }
                // 성공적으로 삭제된 후 로컬 상태 업데이트
                setConsultations(consultations.filter(consultation => !selectedConsultations.includes(consultation.id)));
                setSelectedConsultations([]);
                alert(`${selectedConsultations.length}개의 상담내역이 성공적으로 삭제되었습니다.`);
                // 데이터 다시 로드 (서버와 동기화)
                await loadConsultations();
            }
            catch (error) {
                console.error('상담 삭제 중 오류 발생:', error);
                const errorMessage = ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || error.message || '상담 삭제 중 오류가 발생했습니다.';
                alert(errorMessage);
            }
            finally {
                setLoading(false);
            }
        }
    };
    // 검색어 변경 처리
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };
    // 검색 컬럼 변경 처리
    const handleSearchColumnChange = (e) => {
        setSearchColumn(e.target.value);
    };
    // 상태 변경 처리
    const handleStatusChange = (e) => {
        setStatus(e.target.value);
    };
    // 엔터키 검색 처리
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };
    // 검색 실행
    const handleSearch = () => {
        // 서버에서 데이터 다시 로드
        loadConsultations();
        setCurrentPage(1);
    };
    // 상세 검색 토글
    const toggleDetailSearch = () => {
        setShowDetailSearch(!showDetailSearch);
    };
    // 필터 초기화
    const clearFilters = () => {
        setSearchTerm('');
        setSearchColumn('recipient_name');
        setStatus('');
        setStartDate('');
        setEndDate('');
        setDateColumn('created_at');
    };
    // Excel 다운로드 함수 수정
    const downloadExcel = async () => {
        try {
            // 선택된 상담이 있으면 선택된 것만, 없으면 전체 상담 데이터 사용
            const selectedConsultationData = selectedConsultations.length > 0
                ? consultations.filter(consultation => selectedConsultations.includes(consultation.id))
                : consultations;
            if (selectedConsultationData.length === 0) {
                alert('다운로드할 데이터가 없습니다.');
                return;
            }
            const workbook = new ExcelJS.Workbook();
            // 전체 시트
            const allSheet = workbook.addWorksheet('전체');
            // 전체 시트 헤더
            allSheet.addRow(['상담내역', `${new Date().toISOString().split('T')[0]}`]);
            allSheet.addRow([]); // 빈 행
            const allHeaders = [
                'No.', '상담일', '구매일', '택배사', '쇼핑몰ID', '수취인', '연락처', '리셀러명', '연락처', '송장',
                '처리유형', '출고자', '상담접수', '주소', '상품코드', '상품명', '수량', '접수내용', '은행명', '계좌번호',
                '입금자명', '금액', '출고(box)', '출고', '출고운임', '반품(box)', '반품수거', '반품운임', '처리 택배 업체',
                '택배업체', '상품값', '택배비', '포장비'
            ];
            allSheet.addRow(allHeaders);
            // 전체 시트 데이터
            selectedConsultationData.forEach((consultation, index) => {
                const row = [
                    index + 1,
                    consultation.created_at || '',
                    '',
                    consultation.courier_company || consultation.send_courier_company || '',
                    consultation.mall_name || consultation.mall_id || '',
                    consultation.recipient_name || '',
                    consultation.contact || '',
                    '',
                    '',
                    '',
                    consultation.process_type || '',
                    '',
                    '',
                    consultation.address || '',
                    getAllProductCodes(consultation),
                    getAllProductNames(consultation),
                    getProductCount(consultation),
                    consultation.claim_content || '',
                    consultation.bank_name || '',
                    consultation.account_number || '',
                    consultation.depositor || '',
                    consultation.refund_amount || 0,
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    consultation.courier_company || consultation.send_courier_company || '',
                    '',
                    (consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0,
                    '' // 포장비
                ];
                allSheet.addRow(row);
            });
            // 택배사접수 시트
            const courierSheet = workbook.addWorksheet('택배사접수');
            const courierHeaders = [
                'No.', '상담일', '구매일', '택배사', '쇼핑몰ID', '수취인', '연락처', '리셀러명', '연락처', '송장',
                '처리유형', '출고자', '상담접수', '주소', '상품코드', '상품명', '수량', '접수내용', '은행명', '계좌번호',
                '입금자명', '금액', '출고(box)', '출고', '출고운임', '반품(box)', '반품수거', '반품운임', '처리 택배 업체',
                '택배업체', '상품값', '택배비', '포장비'
            ];
            courierSheet.addRow(courierHeaders);
            selectedConsultationData.forEach((consultation, index) => {
                const row = [
                    index + 1,
                    consultation.created_at || '',
                    '',
                    consultation.courier_company || consultation.send_courier_company || '',
                    consultation.mall_name || consultation.mall_id || '',
                    consultation.recipient_name || '',
                    consultation.contact || '',
                    '',
                    '',
                    '',
                    consultation.process_type || '',
                    '',
                    '',
                    consultation.address || '',
                    getAllProductCodes(consultation),
                    getAllProductNames(consultation),
                    getProductCount(consultation),
                    consultation.claim_content || '',
                    consultation.bank_name || '',
                    consultation.account_number || '',
                    consultation.depositor || '',
                    consultation.refund_amount || 0,
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    consultation.courier_company || '',
                    '',
                    (consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0,
                    '' // 포장비
                ];
                courierSheet.addRow(row);
            });
            // 클레임대장 시트
            const claimSheet = workbook.addWorksheet('클레임대장');
            claimSheet.addRow(['일별 CLAIM 현황', `${new Date().toISOString().split('T')[0]}`]);
            claimSheet.addRow([]); // 빈 행
            const claimHeaders = [
                'No.', '상담일', '구매일', '택배사', '쇼핑몰ID', '수취인', '연락처', '리셀러명(주문자)', '연락처', '송장',
                '처리유형', '출고자', '상담접수', '주소', '상품코드', '상품명', '수량', '접수내용', '은행명', '계좌번호',
                '입금자명', '금액', '출고(box)', '출고', '출고운임', '반품(box)', '반품수거', '반품운임', '처리 택배 업체',
                '택배업체', '상품값', '택배비', '포장비', '접수일시', '처리일시', '완료일시', '상태', '비고1', '비고2',
                '처리자', '확인자', '배송메모', '특이사항', '추가비용'
            ];
            claimSheet.addRow(claimHeaders);
            selectedConsultationData.forEach((consultation, index) => {
                const row = [
                    index + 1,
                    consultation.created_at || '',
                    '',
                    consultation.courier_company || '',
                    consultation.mall_name || consultation.mall_id || '',
                    consultation.recipient_name || '',
                    consultation.contact || '',
                    '',
                    '',
                    '',
                    consultation.process_type || '',
                    '',
                    '',
                    consultation.address || '',
                    getAllProductCodes(consultation),
                    getAllProductNames(consultation),
                    getProductCount(consultation),
                    consultation.claim_content || '',
                    consultation.bank_name || '',
                    consultation.account_number || '',
                    consultation.depositor || '',
                    consultation.refund_amount || 0,
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    consultation.courier_company || consultation.send_courier_company || '',
                    '',
                    (consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0,
                    '',
                    '',
                    '',
                    '',
                    consultation.status || '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '' // 추가비용
                ];
                claimSheet.addRow(row);
            });
            // 지결 시트
            const settlementSheet = workbook.addWorksheet('지결');
            settlementSheet.addRow(['지출결의서', `${new Date().toISOString().split('T')[0]}`]);
            settlementSheet.addRow([]); // 빈 행
            const settlementHeaders = [
                'No.', '상담일', '구매일', '쇼핑몰ID', '수취인명', '처리유형', '상담자', '상품코드', '상품명', '수량',
                '단가', '합계', '비고', '확인', '결재', '승인'
            ];
            settlementSheet.addRow(settlementHeaders);
            const settlementData = selectedConsultationData.filter(consultation => consultation.claim_type === '지결');
            settlementData.forEach((consultation, index) => {
                const row = [
                    index + 1,
                    consultation.created_at || '',
                    '',
                    consultation.mall_name || consultation.mall_id || '',
                    consultation.recipient_name || '',
                    consultation.process_type || '',
                    '',
                    getAllProductCodes(consultation),
                    getAllProductNames(consultation),
                    getProductCount(consultation),
                    '',
                    '',
                    '',
                    '',
                    '',
                    '' // 승인
                ];
                settlementSheet.addRow(row);
            });
            // 사후관리대장 시트
            const afterServiceSheet = workbook.addWorksheet('사후관리대장');
            afterServiceSheet.addRow(['*입고건 날짜별 자동수집']);
            afterServiceSheet.addRow([]); // 빈 행
            const afterServiceHeaders = [
                '순번', '상담일', '출고일', '택배사', '쇼핑몰ID', '수취인', '처리유형', '품명', '접수내용',
                '수거택배사', '상태', '처리일', '비고', '담당자'
            ];
            afterServiceSheet.addRow(afterServiceHeaders);
            selectedConsultationData.forEach((consultation, index) => {
                const row = [
                    index + 1,
                    consultation.created_at || '',
                    '',
                    consultation.courier_company || '',
                    consultation.mall_name || consultation.mall_id || '',
                    consultation.recipient_name || '',
                    consultation.process_type || '',
                    getAllProductNames(consultation),
                    consultation.claim_content || '',
                    '',
                    consultation.status || '',
                    '',
                    '',
                    '' // 담당자
                ];
                afterServiceSheet.addRow(row);
            });
            // 수입처 시트
            const importSheet = workbook.addWorksheet('수입처');
            importSheet.addRow(['수입처 청구', `${new Date().toISOString().split('T')[0]}`]);
            importSheet.addRow([]); // 빈 행
            const importHeaders = [
                'No.', '상담일', '구매일', '택배사', '쇼핑몰ID', '수취인', '연락처', '리셀러명', '연락처', '송장',
                '처리유형', '출고자', '상담접수', '주소', '상품코드', '상품명', '수량', '접수내용', '은행명', '계좌번호',
                '입금자명', '금액', '출고(box)', '출고', '출고운임', '반품(box)', '반품수거', '반품운임', '처리 택배 업체',
                '택배업체', '상품값', '택배비', '포장비', '청구금액'
            ];
            importSheet.addRow(importHeaders);
            const importData = selectedConsultationData.filter(consultation => consultation.refund_amount && consultation.refund_amount > 0);
            importData.forEach((consultation, index) => {
                const row = [
                    index + 1,
                    consultation.created_at || '',
                    '',
                    consultation.courier_company || '',
                    consultation.mall_name || consultation.mall_id || '',
                    consultation.recipient_name || '',
                    consultation.contact || '',
                    '',
                    '',
                    '',
                    consultation.process_type || '',
                    '',
                    '',
                    consultation.address || '',
                    getAllProductCodes(consultation),
                    getAllProductNames(consultation),
                    getProductCount(consultation),
                    consultation.claim_content || '',
                    consultation.bank_name || '',
                    consultation.account_number || '',
                    consultation.depositor || '',
                    consultation.refund_amount || 0,
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    consultation.courier_company || '',
                    '',
                    (consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0,
                    '',
                    0 // 청구금액
                ];
                importSheet.addRow(row);
            });
            // 천일택배 시트
            const cheonilSheet = workbook.addWorksheet('천일');
            const cheonilHeaders = [
                '수취인', '전화번호', '휴대폰', '우편번호', '주소', '품명', '수량', '운임구분', '운송구분', '배송메세지',
                '업체명', '주문번호', '거래처전화번호', '적용품명', '내용'
            ];
            cheonilSheet.addRow(cheonilHeaders);
            const cheonilData = selectedConsultationData.filter(consultation => (consultation.courier_company && consultation.courier_company.includes('천일')) || (consultation.send_courier_company && consultation.send_courier_company.includes('천일')));
            cheonilData.forEach(consultation => {
                const row = [
                    consultation.recipient_name || '',
                    consultation.contact || '',
                    '',
                    consultation.zip_code || '',
                    consultation.address || '',
                    getAllProductNames(consultation),
                    getProductCount(consultation),
                    (consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0,
                    getCheonilDeliveryCode((consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0),
                    '',
                    '',
                    consultation.order_number || '',
                    '',
                    '',
                    consultation.claim_content || '' // 내용
                ];
                cheonilSheet.addRow(row);
            });
            // 한진택배 시트
            const hanjinSheet = workbook.addWorksheet('한진');
            const hanjinHeaders = [
                '받는사람', '주소', '전화번호1', '전화번호2', '수량(A타입)', '운임', '운임구분', '상품명1',
                '배송메시지', '우편번호', '특기사항'
            ];
            hanjinSheet.addRow(hanjinHeaders);
            const hanjinData = selectedConsultationData.filter(consultation => (consultation.courier_company && consultation.courier_company.includes('한진')) || (consultation.send_courier_company && consultation.send_courier_company.includes('한진')));
            hanjinData.forEach(consultation => {
                const row = [
                    consultation.recipient_name || '',
                    consultation.address || '',
                    consultation.contact || '',
                    '',
                    getProductCount(consultation),
                    (consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0,
                    getBoxTypCdByFee((consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0),
                    getAllProductNames(consultation),
                    '',
                    consultation.zip_code || '',
                    '' // 특기사항
                ];
                hanjinSheet.addRow(row);
            });
            // 경동택배 시트
            const gyeongdongSheet = workbook.addWorksheet('경동');
            const gyeongdongHeaders = [
                '받는사람', '전화번호', '전화번호2', '주소', '\u3000', '품목명', '수량(A타입)', '포장상태', '개별단가(만원)',
                '배송구분', '운임', '특별운임', '기타운임', '상품명', '메모2', '메모3', '메모4', '메모5', '내용'
            ];
            gyeongdongSheet.addRow(gyeongdongHeaders);
            const gyeongdongData = selectedConsultationData.filter(consultation => (consultation.courier_company && consultation.courier_company.includes('경동')) || (consultation.send_courier_company && consultation.send_courier_company.includes('경동')));
            gyeongdongData.forEach(consultation => {
                const row = [
                    consultation.recipient_name || '',
                    consultation.contact || '',
                    '',
                    consultation.address || '',
                    '',
                    getAllProductNames(consultation),
                    getProductCount(consultation),
                    '',
                    '',
                    '',
                    (consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0,
                    '',
                    '',
                    getAllProductNames(consultation),
                    '',
                    '',
                    '',
                    '',
                    consultation.claim_content || '' // 내용
                ];
                gyeongdongSheet.addRow(row);
            });
            // Excel 파일 다운로드
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const fileName = selectedConsultations.length > 0
                ? `선택한_상담처리_${new Date().toISOString().split('T')[0]}.xlsx`
                : `전체_상담처리_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
            const downloadMessage = selectedConsultations.length > 0
                ? `선택한 ${selectedConsultations.length}개 상담의 Excel 파일이 다운로드되었습니다.`
                : 'Excel 파일이 다운로드되었습니다.';
            alert(downloadMessage);
        }
        catch (error) {
            console.error('Excel 다운로드 오류:', error);
            alert('Excel 다운로드 중 오류가 발생했습니다.');
        }
    };
    // 엑셀 템플릿 다운로드
    const handleDownloadTemplate = () => {
        const template = [
            {
                order_date: '2024-05-15',
                recipient_name: '수취인명',
                address: '주소',
                contact: '핸드폰번호',
                product_name: '상품명',
                mall_id: '쇼핑몰ID',
                courier_company: '택배사',
                process_type: '처리유형',
                refund_amount: '환불금액',
                return_fee: '반품비',
                courier_fee: '택배비'
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(template);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '상담처리_템플릿');
        XLSX.writeFile(workbook, '상담처리_템플릿.xlsx');
    };
    // 모달 토글
    const toggleAddModal = () => {
        setShowAddModal(!showAddModal);
    };
    const toggleUploadModal = () => {
        setShowUploadModal(!showUploadModal);
        if (showUploadModal) {
            setUploadedConsultations([]);
            setUploadError('');
        }
    };
    // 상담처리 등록 모달 열기 (ClaimModal 사용)
    const toggleConsultationModal = () => {
        setClaimOrder(null); // 새 등록이므로 기존 데이터 없음
        setShowClaimModal(true);
    };
    // 클레임 모달 열기 (기존 상담 데이터로)
    const openClaimModal = (consultation) => {
        // 상품 정보 처리 개선
        let processedProducts = [];
        if (consultation.products && Array.isArray(consultation.products) && consultation.products.length > 0) {
            processedProducts = consultation.products.map((product, index) => {
                const processedProduct = {
                    productCode: product.product_code || product.productCode || '',
                    productName: product.product_name || product.productName || '상품명 없음',
                    productType: product.product_type || product.productType || '일반상품',
                    productOption: product.product_option || product.productOption || '',
                    originalQuantity: parseInt(product.original_quantity || product.originalQuantity || 1),
                    claimQuantity: parseInt(product.claim_quantity || product.claimQuantity || 1),
                    unitPrice: parseFloat(product.unit_price || product.unitPrice || 0),
                    totalPrice: parseFloat(product.total_price || product.totalPrice || 0),
                    claimAmount: parseFloat(product.claim_amount || product.claimAmount || 0),
                    productClaimReason: product.product_claim_reason || product.productClaimReason || consultation.claim_content || '',
                    productStatus: product.product_status || product.productStatus || consultation.status || '대기'
                };
                return processedProduct;
            });
        }
        else {
            // 기본 상품 정보 생성 (실제 상담 데이터 기반)
            processedProducts = [{
                    productCode: '',
                    productName: consultation.product_name || '상품명 없음',
                    productType: '일반상품',
                    productOption: '',
                    originalQuantity: 1,
                    claimQuantity: 1,
                    unitPrice: 0,
                    totalPrice: 0,
                    claimAmount: consultation.refund_amount || 0,
                    productClaimReason: consultation.claim_content || '',
                    productStatus: consultation.status || '대기'
                }];
        }
        // 상담 데이터를 클레임 모달에 맞는 형식으로 변환
        const orderData = {
            consultationId: consultation.id,
            consultationNumber: consultation.consultation_number,
            orderNumber: consultation.order_number,
            orderDate: consultation.order_date,
            mallName: consultation.mall_name,
            customerName: consultation.customer_name || consultation.recipient_name || '',
            recipientName: consultation.recipient_name || '',
            contact: consultation.contact || '',
            address: consultation.address || '',
            zipCode: consultation.zip_code || '',
            // 클레임 정보
            processType: consultation.process_type || '환불',
            claimType: consultation.claim_type || '단순변심',
            claimReason: consultation.claim_reason || '',
            claimContent: consultation.claim_content || '',
            claimComp: consultation.claim_comp || '',
            receiver: consultation.receiver || '',
            priority: consultation.priority || '보통',
            // 환불 정보
            refundType: consultation.refund_type || '계좌이체',
            bankName: consultation.bank_name || '',
            accountNumber: consultation.account_number || '',
            depositor: consultation.depositor || '',
            refundAmount: consultation.refund_amount || 0,
            // 배송 정보
            deliveryBoxCount: consultation.return_box || 0,
            deliveryCourierCompany: consultation.courier_company || '',
            deliveryCourierFee: consultation.courier_fee || 0,
            deliveryFeeType: consultation.return_fee_type || '고객부담',
            courierFee: consultation.courier_fee || 0,
            // 반품 정보
            returnBoxCount: consultation.send_box || 0,
            returnCourierCompany: consultation.send_courier_company || consultation.courier_company || '',
            returnCourierFee: consultation.send_courier_fee || 0,
            returnFeeType: consultation.send_fee_type || '고객부담',
            returnFee: consultation.send_courier_fee || 0,
            // 처리된 상품 정보 사용
            products: processedProducts,
            // 집계 정보
            totalProductCount: processedProducts.length,
            totalClaimAmount: processedProducts.reduce((sum, product) => sum + (product.claimAmount || 0), 0) || consultation.total_claim_amount || consultation.refund_amount || 0,
            // 시스템 정보
            status: consultation.status || '접수',
            createdAt: consultation.created_at || '',
            updatedAt: consultation.updated_at || '',
            createdBy: consultation.created_by || '',
            updatedBy: consultation.updated_by || ''
        };
        setClaimOrder(orderData);
        setShowClaimModal(true);
    };
    // 클레임 제출 핸들러
    const handleClaimSubmit = async (claimData) => {
        var _a, _b, _c, _d;
        setIsClaimLoading(true);
        try {
            // 기존 상담 수정인지 새 등록인지 확인
            const isEditMode = claimData.consultationId > 0;
            const apiUrl = isEditMode
                ? `/api/consultations/update/${claimData.consultationId}`
                : `/api/consultations`;
            const httpMethod = isEditMode ? 'post' : 'post';
            debugger;
            // API 요청 데이터 구성
            const requestData = {
                // 기본 정보
                orderNumber: claimData.orderNumber,
                orderDate: claimData.orderDate,
                mallName: claimData.mallName,
                customerName: claimData.customerName,
                recipientName: claimData.recipientName,
                contact: claimData.contact,
                address: claimData.address,
                zipCode: claimData.zipCode,
                // 클레임 정보
                processType: claimData.processType,
                claimType: claimData.claimType,
                claimReason: claimData.claimReason,
                claimContent: claimData.claimContent,
                claimComp: claimData.claimComp,
                receiver: claimData.receiver,
                priority: claimData.priority || 'normal',
                // 환불 정보
                refundType: claimData.refundType,
                bankName: claimData.bankName,
                accountNumber: claimData.accountNumber,
                depositor: claimData.depositor,
                refundAmount: claimData.refundAmount,
                // 배송 정보
                courier_company: claimData.deliveryCourierCompany,
                courier_fee: claimData.courierFee || 0,
                return_box: claimData.deliveryBoxCount || 0,
                return_fee_type: claimData.deliveryFeeType || '고객부담',
                // 반품 정보
                send_courier_fee: claimData.returnFee || 0,
                send_box: claimData.returnBoxCount || 0,
                send_fee_type: claimData.returnFeeType || '고객부담',
                send_courier_company: claimData.returnCourierCompany,
                // 상품 목록
                products: claimData.products || [],
                // 자동 생성된 상담번호 및 집계 정보
                consultationNumber: claimData.consultationNumber,
                totalProductCount: claimData.totalProductCount || 0,
                totalClaimAmount: claimData.totalClaimAmount || 0,
                // 상태 정보
                status: claimData.status || '접수',
                // 생성자/수정자 정보
                createdBy: isEditMode ? claimData.createdBy : 'system',
                updatedBy: 'system'
            };
            // API 호출
            const response = await api[httpMethod](apiUrl, requestData);
            if (response.data && response.data.success) {
                alert(`고객상담이 성공적으로 ${isEditMode ? '수정' : '등록'}되었습니다.`);
                setShowClaimModal(false);
                setClaimOrder(null);
                await loadConsultations(); // 상담 목록 새로고침
            }
            else {
                throw new Error(((_a = response.data) === null || _a === void 0 ? void 0 : _a.message) || `${isEditMode ? '수정' : '등록'}에 실패했습니다.`);
            }
        }
        catch (error) {
            console.error('클레임 처리 실패:', error);
            console.error('에러 응답:', (_b = error.response) === null || _b === void 0 ? void 0 : _b.data);
            alert(((_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.message) || '클레임 처리에 실패했습니다.');
        }
        finally {
            setIsClaimLoading(false);
        }
    };
    // 클레임 모달 닫기
    const handleClaimClose = () => {
        setShowClaimModal(false);
        setClaimOrder(null);
    };
    // 클레임 성공 핸들러
    const handleClaimSuccess = () => {
        loadConsultations(); // 데이터 새로고침
        alert('상담처리가 성공적으로 등록되었습니다!');
        setShowClaimModal(false);
    };
    // 파일 업로드 처리
    const handleFileUpload = (e) => {
        const files = e.target.files;
        if (!files || files.length === 0)
            return;
        const file = files[0];
        const reader = new FileReader();
        reader.onload = (event) => {
            var _a;
            try {
                const data = (_a = event.target) === null || _a === void 0 ? void 0 : _a.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(worksheet);
                // 데이터 검증 및 변환
                const uploadedData = json.map((row, index) => ({
                    id: index + 1,
                    order_number: row.order_number || '',
                    order_date: row.order_date || '',
                    mall_id: row.mall_id || '',
                    product_name: row.product_name || '',
                    recipient_name: row.recipient_name || '',
                    contact: row.contact || '',
                    address: row.address || '',
                    process_type: row.process_type || '',
                    receiver: row.receiver || '',
                    claim_content: row.claim_content || '',
                    bank_name: row.bank_name || '',
                    account_number: row.account_number || '',
                    depositor: row.depositor || '',
                    refund_amount: Number(row.refund_amount) || 0,
                    return_box: Number(row.return_box) || 0,
                    return_fee: Number(row.return_fee) || 0,
                    return_fee_type: row.return_fee_type || '',
                    courier_company: row.courier_company || '',
                    courier_fee: Number(row.courier_fee) || 0,
                    status: '접수',
                    created_at: new Date().toISOString().split('T')[0],
                    updated_at: new Date().toISOString().split('T')[0],
                }));
                setUploadedConsultations(uploadedData);
                setUploadError('');
                toggleUploadModal();
            }
            catch (error) {
                console.error('파일 처리 오류:', error);
                setUploadError('파일 처리 중 오류가 발생했습니다. 올바른 형식의 엑셀 파일인지 확인해주세요.');
            }
        };
        reader.onerror = () => {
            setUploadError('파일을 읽는 중 오류가 발생했습니다.');
        };
        reader.readAsBinaryString(file);
    };
    // 업로드된 데이터 저장
    const handleSaveUploadedData = () => {
        if (uploadedConsultations.length === 0) {
            alert('업로드할 데이터가 없습니다.');
            return;
        }
        // 기존 ID를 변경하여 저장
        const newData = uploadedConsultations.map((item, index) => ({
            ...item,
            id: consultations.length + index + 1
        }));
        setConsultations([...consultations, ...newData]);
        toggleUploadModal();
        alert(`${newData.length}개의 상담내역이 성공적으로 업로드되었습니다.`);
    };
    // 새 상담내역 필드 변경 처리
    const handleNewConsultationChange = (e) => {
        const { name, value } = e.target;
        setNewConsultation({
            ...newConsultation,
            [name]: value
        });
    };
    // 새 상담내역 추가
    const handleAddConsultation = () => {
        // 필수 필드 검증
        if (!newConsultation.recipient_name || !newConsultation.product_name) {
            alert('수취인, 상품명은 필수 입력 항목입니다.');
            return;
        }
        const newId = consultations.length + 1;
        const newData = {
            ...newConsultation,
            id: newId,
            created_at: new Date().toISOString().split('T')[0],
            updated_at: new Date().toISOString().split('T')[0]
        };
        setConsultations([...consultations, newData]);
        toggleAddModal();
        // 새 상담내역 폼 초기화
        setNewConsultation({
            id: 0,
            order_number: '',
            order_date: '',
            mall_id: '',
            product_name: '',
            recipient_name: '',
            contact: '',
            address: '',
            process_type: '',
            receiver: '',
            claim_content: '',
            bank_name: '',
            account_number: '',
            depositor: '',
            refund_amount: 0,
            return_box: 0,
            return_fee: 0,
            return_fee_type: '',
            courier_company: '',
            courier_fee: 0,
            status: '접수',
            created_at: '',
            updated_at: '',
        });
        alert('상담내역이 성공적으로 등록되었습니다.');
    };
    // CS 메모 관련 함수
    const handleNewMemoChange = (e) => {
        const { name, value } = e.target;
        setNewMemo({
            ...newMemo,
            [name]: value
        });
    };
    const handleAddMemo = () => {
        if (!newMemo.manager || !newMemo.content) {
            alert('담당자와 내용은 필수 입력 항목입니다.');
            return;
        }
        const newId = (csMemos.length + 1).toString();
        const newMemoData = {
            ...newMemo,
            id: newId,
            date: newMemo.date || new Date().toISOString().split('T')[0]
        };
        setCsMemos([...csMemos, newMemoData]);
        // 메모 폼 초기화
        setNewMemo({
            id: '',
            date: new Date().toISOString().split('T')[0],
            manager: '',
            content: ''
        });
    };
    const handleDeleteMemo = (id) => {
        if (window.confirm('이 메모를 삭제하시겠습니까?')) {
            setCsMemos(csMemos.filter(memo => memo.id !== id));
        }
    };
    // 페이지네이션 처리
    const indexOfLastItem = itemsPerPage === -1 ? filteredConsultations.length : currentPage * itemsPerPage;
    const indexOfFirstItem = itemsPerPage === -1 ? 0 : indexOfLastItem - itemsPerPage;
    const currentItems = filteredConsultations.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = itemsPerPage === -1 ? 1 : Math.ceil(filteredConsultations.length / itemsPerPage);
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };
    // 엑셀 업로드 핸들러
    const handleExcelUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            if (!e.target)
                return;
            const wb = XLSX.read(e.target.result, { type: 'binary' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(ws);
            if (data.length > 0) {
                alert('엑셀 데이터가 입력폼에 반영되었습니다.');
            }
        };
        reader.readAsBinaryString(file);
        return false;
    };
    // 저장(등록) API 연동
    const handleSave = async () => {
        try {
            setLoading(true);
            await api.post(`/api/consultations`, newConsultation);
            alert('상담접수 정보가 저장되었습니다.');
            setNewConsultation({
                id: 0,
                order_number: '',
                order_date: '',
                mall_id: '',
                product_name: '',
                recipient_name: '',
                contact: '',
                address: '',
                process_type: '',
                receiver: '',
                claim_content: '',
                bank_name: '',
                account_number: '',
                depositor: '',
                refund_amount: 0,
                return_fee: 0,
                return_box: 0,
                return_fee_type: '',
                courier_company: '',
                courier_fee: 0,
                status: '접수',
                created_at: '',
                updated_at: '',
            });
            loadConsultations(); // 데이터 다시 로드
        }
        catch (err) {
            alert('저장 실패: 필수값을 확인하세요.');
        }
        finally {
            setLoading(false);
        }
    };
    // 입고 처리 함수
    const handleIncoming = async () => {
        if (selectedConsultations.length === 0) {
            alert('처리할 항목을 선택해주세요.');
            return;
        }
        try {
            setLoading(true);
            const response = await api.post(`/api/consultation-incoming`, {
                consultationIds: selectedConsultations
            });
            if (response.data && response.data.success) {
                alert(`${selectedConsultations.length}개 항목이 입고 처리되었습니다.`);
                setSelectedConsultations([]);
                loadConsultations(); // 데이터 새로고침
            }
            else {
                alert('입고 처리에 실패했습니다.');
            }
        }
        catch (error) {
            console.error('입고 처리 실패:', error);
            alert('입고 처리 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    // 출고 처리 함수
    const handleOutgoing = async () => {
        if (selectedConsultations.length === 0) {
            alert('처리할 항목을 선택해주세요.');
            return;
        }
        try {
            setLoading(true);
            const response = await api.post(`/api/consultation-outgoing`, {
                consultationIds: selectedConsultations
            });
            if (response.data && response.data.success) {
                alert(`${selectedConsultations.length}개 항목이 출고 처리되었습니다.`);
                setSelectedConsultations([]);
                loadConsultations(); // 데이터 새로고침
            }
            else {
                alert('출고 처리에 실패했습니다.');
            }
        }
        catch (error) {
            console.error('출고 처리 실패:', error);
            alert('출고 처리 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    // 주문 등록 처리 함수 - 상품별로 개별 주문 생성
    const handleRegisterToOrders = async () => {
        var _a, _b, _c;
        if (selectedConsultations.length === 0) {
            alert('주문으로 등록할 항목을 선택해주세요.');
            return;
        }
        try {
            setLoading(true);
            let totalOrdersCreated = 0;
            const processResults = [];
            for (const consultationId of selectedConsultations) {
                try {
                    // 선택된 상담 정보 찾기
                    const consultation = filteredConsultations.find(c => c.id === consultationId);
                    if (!consultation) {
                        console.error(`상담 ID ${consultationId}를 찾을 수 없습니다.`);
                        continue;
                    }
                    // 상품 배열 확인 및 처리
                    let products = [];
                    if (consultation.products && Array.isArray(consultation.products) && consultation.products.length > 0) {
                        products = consultation.products;
                    }
                    else {
                        // 기본 상품 정보 생성
                        products = [{
                                product_code: '',
                                product_name: consultation.product_name || '상품명 없음',
                                quantity: consultation.total_product_count || 1,
                                unit_price: 0,
                                total_price: consultation.refund_amount || 0
                            }];
                    }
                    // 각 상품별로 개별 주문 생성
                    for (let i = 0; i < products.length; i++) {
                        const product = products[i];
                        // 주문번호 생성 (원래 상담 주문번호 + 상품 순번)
                        const orderNumber = products.length > 1
                            ? `${consultation.order_number}_${String(i + 1).padStart(2, '0')}`
                            : consultation.order_number;
                        const orderData = {
                            orderNumber: ((_a = consultation.consultation_number) === null || _a === void 0 ? void 0 : _a.replace(/-/g, '')) || consultation.order_number || '',
                            orderDate: consultation.order_date
                                ? consultation.order_date.replace(/-/g, '').split('T')[0].replace(/-/g, '')
                                : new Date().toISOString().split('T')[0].replace(/-/g, ''),
                            orderType: '상담처리',
                            orderStatus: '처리완료',
                            customerName: consultation.customer_name || consultation.recipient_name || '',
                            recipientName: consultation.recipient_name || '',
                            zipCode: consultation.zip_code || '',
                            address: consultation.address || '',
                            addressConvert: consultation.address || '',
                            mobilePhone: consultation.contact || '',
                            phone: consultation.contact || '',
                            totalAmount: product.total_price || product.claim_amount || consultation.refund_amount || 0,
                            totalShippingFee: consultation.courier_fee || 0,
                            totalDiscount: 0,
                            paymentStatus: '완료',
                            shippingMethod: consultation.return_fee_type || '선불',
                            shippingStatus: '출고대기',
                            message: `상담처리: ${consultation.process_type || ''}`,
                            channelId: consultation.mall_name || consultation.mall_id || '',
                            channelOrderId: ((_b = consultation.consultation_number) === null || _b === void 0 ? void 0 : _b.replace(/-/g, '')) || consultation.order_number || '',
                            mallId: consultation.mall_name || consultation.mall_id || '',
                            sapangNetOrderNumber: ((_c = consultation.consultation_number) === null || _c === void 0 ? void 0 : _c.replace(/-/g, '')) || consultation.order_number || '',
                            productCode: product.product_code || product.productCode || '',
                            productName: product.product_name || product.productName || consultation.product_name || '',
                            quantity: product.claimQuantity || consultation.return_box || 1,
                            unitPrice: product.unit_price || product.unitPrice || 0,
                            totalPrice: product.total_price || product.totalPrice || product.claim_amount || product.claimAmount || 0,
                            shippingFee: (consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0,
                            optionInfo: product.product_option || product.productOption || '',
                            status: '등록완료',
                            shipComp: consultation.courier_company || '',
                            shipCompFee: (consultation.courier_fee == 0 ? consultation.send_courier_fee : consultation.courier_fee) || 0,
                            shippingQty: consultation.return_box || 1,
                            deliveryAreaType: 'N',
                            consultationId: consultationId
                        };
                        // 개별 주문 생성 API 호출
                        const response = await api.post(`/api/orders/simple`, orderData);
                        if (response.data && response.data.success) {
                            totalOrdersCreated++;
                        }
                        else {
                            console.error(`주문 생성 실패: ${orderNumber}`, response.data);
                        }
                    }
                    processResults.push({
                        consultationId,
                        orderCount: products.length,
                        status: 'success'
                    });
                }
                catch (error) {
                    console.error(`상담 ID ${consultationId} 처리 실패:`, error);
                    processResults.push({
                        consultationId,
                        orderCount: 0,
                        status: 'failed',
                        error: error instanceof Error ? error.message : String(error)
                    });
                }
            }
            if (totalOrdersCreated > 0) {
                alert(`${totalOrdersCreated}개의 주문이 성공적으로 등록되었습니다.`);
                setSelectedConsultations([]);
                // 필요시 상담 목록 새로고침
                // loadConsultations();
            }
            else {
                alert('주문 등록에 실패했습니다.');
            }
        }
        catch (error) {
            console.error('주문 등록 처리 중 오류:', error);
            alert('주문 등록 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    // 현재 페이지 데이터 로깅
    currentItems.forEach((consultation, index) => {
    });
    // 자동전송 선택 기능 추가
    const handleAutoSelect = () => {
        // 현재 페이지의 데이터에서 조건에 맞는 항목들 찾기
        const autoSelectedIds = currentItems.filter(consultation => {
            // 처리유형이 "맞교환" 또는 "출고"인지 확인
            const processType = consultation.process_type || '';
            const isValidProcessType = processType.includes('맞교환') || processType.includes('출고');
            // 상품명이 "부품"으로 시작하지 않는지 확인
            const productName = getAllProductNames(consultation) || consultation.product_name || '';
            const isNotPartsProduct = !productName.startsWith('부품');
            // 상태가 "접수"인지 확인
            const status = consultation.status || '';
            const isReceptionStatus = status === '접수';
            return isValidProcessType && isNotPartsProduct && isReceptionStatus;
        }).map(consultation => consultation.id);
        // 선택된 상담 ID들을 state에 설정
        setSelectedConsultations(autoSelectedIds);
        // 사용자에게 알림
        if (autoSelectedIds.length > 0) {
            alert(`${autoSelectedIds.length}개 항목이 자동 선택되었습니다.`);
        }
        else {
            alert('조건에 맞는 항목이 없습니다.');
        }
    };
    return (_jsxs("div", { className: "flex flex-col h-full -m-3", children: [_jsx("div", { className: "flex-shrink-0 bg-gray-100 p-3", children: _jsxs("div", { className: "flex justify-between items-center", children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900", children: pageTitle }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: handleDownloadTemplate, children: "\uD15C\uD50C\uB9BF \uB2E4\uC6B4\uB85C\uB4DC" }), _jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: downloadExcel, children: "\uC0C1\uB2F4\uCC98\uB9AC \uC5D1\uC140 \uB2E4\uC6B4\uB85C\uB4DC" }), _jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, children: "\uC5D1\uC140 \uC5C5\uB85C\uB4DC" }), _jsx("button", { className: "h-9 px-4 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700", onClick: toggleConsultationModal, children: "\uC0C1\uB2F4\uCC98\uB9AC \uB4F1\uB85D" }), _jsx("input", { type: "file", accept: ".xlsx, .xls", ref: fileInputRef, onChange: handleFileUpload, className: "hidden" })] })] }) }), _jsxs("div", { className: "flex-1 overflow-auto px-3 pb-3 bg-gray-100", children: [_jsx("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm p-4 mb-3", children: _jsxs("div", { className: "flex items-center gap-3 flex-wrap", children: [_jsxs("select", { className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: searchColumn, onChange: handleSearchColumnChange, children: [_jsx("option", { value: "recipient_name", children: "\uC218\uCDE8\uC778" }), _jsx("option", { value: "orderer", children: "\uC8FC\uBB38\uC790" }), _jsx("option", { value: "product_name", children: "\uC0C1\uD488\uBA85" }), _jsx("option", { value: "trackingNumber", children: "\uC1A1\uC7A5\uBC88\uD638" }), _jsx("option", { value: "productCode", children: "\uC0C1\uD488\uCF54\uB4DC" }), _jsx("option", { value: "receiver", children: "\uB2F4\uB2F9\uC790" })] }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-56", placeholder: "\uAC80\uC0C9\uC5B4\uB97C \uC785\uB825\uD558\uC138\uC694", value: searchTerm, onChange: handleSearchChange, onKeyPress: handleKeyPress }), _jsx("button", { className: "h-9 px-4 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 font-bold", type: "button", onClick: handleSearch, children: "\uAC80\uC0C9" }), _jsxs("select", { className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: status, onChange: handleStatusChange, children: [_jsx("option", { value: "", children: "\uBAA8\uB4E0 \uC0C1\uD0DC" }), _jsx("option", { value: "\uC811\uC218", children: "\uC811\uC218" }), _jsx("option", { value: "\uCC98\uB9AC\uC911", children: "\uCC98\uB9AC\uC911" }), _jsx("option", { value: "\uC644\uB8CC", children: "\uC644\uB8CC" }), _jsx("option", { value: "\uCDE8\uC18C", children: "\uCDE8\uC18C" })] }), _jsx("input", { type: "date", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: startDate, onChange: (e) => setStartDate(e.target.value) }), _jsx("input", { type: "date", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: endDate, onChange: (e) => setEndDate(e.target.value) })] }) }), showDetailSearch && (_jsx("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm p-4 mb-3", children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "font-semibold text-sm", children: "\uB0A0\uC9DC\uAE30\uC900" }), _jsxs("select", { className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: dateColumn, onChange: (e) => setDateColumn(e.target.value), children: [_jsx("option", { value: "created_at", children: "\uC0C1\uB2F4 \uC77C\uC790" }), _jsx("option", { value: "order_date", children: "\uC8FC\uBB38 \uC77C\uC790" }), _jsx("option", { value: "createDate", children: "\uB4F1\uB85D \uC77C\uC790" })] }), _jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: clearFilters, children: "\uD544\uD130 \uCD08\uAE30\uD654" })] }) })), _jsx("div", { className: "flex justify-between items-center mb-3", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: toggleDetailSearch, children: showDetailSearch ? '기본 검색' : '상세 검색' }), _jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: () => {
                                        if (consultations.length > 0) {
                                            consultations.slice(0, 5).forEach((consultation, index) => {
                                            });
                                        }
                                    }, children: "\uB0A0\uC9DC \uB514\uBC84\uADF8" }), _jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: () => {
                                        const today = new Date().toISOString().split('T')[0];
                                        setStartDate(today);
                                        setEndDate('');
                                    }, children: "\uC624\uB298\uBD80\uD130" }), _jsx("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50", onClick: handleAutoSelect, title: "\uC0C1\uD0DC\uAC00 \uC811\uC218\uC774\uBA74\uC11C \uCC98\uB9AC\uC720\uD615\uC774 \uB9DE\uAD50\uD658/\uCD9C\uACE0\uC774\uACE0 \uC0C1\uD488\uBA85\uC774 \uBD80\uD488\uC73C\uB85C \uC2DC\uC791\uD558\uC9C0 \uC54A\uB294 \uD56D\uBAA9\uC744 \uC790\uB3D9 \uC120\uD0DD", children: "\uC790\uB3D9\uC804\uC1A1 \uC120\uD0DD" }), selectedConsultations.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("button", { className: "h-9 px-4 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700", onClick: handleIncoming, disabled: loading, children: ["\uC785\uACE0 (", selectedConsultations.length, ")"] }), _jsxs("button", { className: "h-9 px-4 bg-green-600 text-white text-sm rounded-md hover:bg-green-700", onClick: handleOutgoing, disabled: loading, children: ["\uCD9C\uACE0 (", selectedConsultations.length, ")"] }), _jsxs("button", { className: "h-9 px-4 bg-cyan-600 text-white text-sm rounded-md hover:bg-cyan-700", onClick: handleRegisterToOrders, disabled: loading, children: ["\uC8FC\uBB38\uB4F1\uB85D (", selectedConsultations.length, ")"] }), _jsxs("button", { className: "h-9 px-4 bg-red-600 text-white text-sm rounded-md hover:bg-red-700", onClick: handleDeleteSelected, children: ["\uC120\uD0DD \uC0AD\uC81C (", selectedConsultations.length, ")"] })] }))] }) }), _jsx("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm", children: _jsx("div", { className: "overflow-auto", children: _jsxs("table", { className: "min-w-full text-sm", style: { minWidth: '1800px' }, children: [_jsx("thead", { className: "bg-gray-50 sticky top-0 z-10", children: _jsxs("tr", { children: [_jsx("th", { className: "px-3 py-2 text-center text-xs font-medium text-gray-500", style: { width: '40px' }, children: _jsx("input", { type: "checkbox", checked: selectedConsultations.length === currentItems.length && currentItems.length > 0, onChange: handleSelectAll, className: "rounded border-gray-300" }) }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '50px' }, children: "No." }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '100px' }, children: renderFilterHeader('상담일', 'created_at') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '100px' }, children: renderFilterHeader('주문일', 'order_date') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '120px' }, children: renderFilterHeader('주문번호', 'order_number') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '100px' }, children: renderFilterHeader('쇼핑몰ID', 'mall_id') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '100px' }, children: renderFilterHeader('수취인', 'recipient_name') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '120px' }, children: "\uC5F0\uB77D\uCC98" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '200px' }, children: renderFilterHeader('상품명', 'product_name') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '100px' }, children: renderFilterHeader('처리유형', 'process_type') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '100px' }, children: renderFilterHeader('접수자', 'receiver') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '100px' }, children: renderFilterHeader('택배사', 'courier_company') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '80px' }, children: "\uD658\uBD88\uAE08\uC561" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '80px' }, children: "\uBC30\uC1A1\uBC15\uC2A4" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '80px' }, children: "\uBC30\uC1A1\uBE44" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '80px' }, children: "\uBC18\uD488\uBC15\uC2A4" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '80px' }, children: "\uBC18\uD488\uBE44" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '80px' }, children: renderFilterHeader('상태', 'status') }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500", style: { width: '200px' }, children: "\uC8FC\uC18C" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500 sticky right-0 bg-gray-50 z-10 border-l-2 border-gray-200", style: { width: '100px' }, children: "\uC791\uC5C5" })] }) }), _jsx("tbody", { className: "divide-y divide-gray-200", children: currentItems.length > 0 ? (currentItems.map((consultation, idx) => (_jsxs("tr", { className: "hover:bg-gray-50", style: {
                                                backgroundColor: consultation.status === '전송완료' ? '#d4edda' : undefined
                                            }, children: [_jsx("td", { className: "px-3 py-2 text-sm text-center", children: _jsx("input", { type: "checkbox", checked: selectedConsultations.includes(consultation.id), onChange: (e) => handleSelectConsultation(e, consultation.id), className: "rounded border-gray-300" }) }), _jsx("td", { className: "px-3 py-2 text-sm text-center", children: consultation.consultation_number }), _jsx("td", { className: "px-3 py-2 text-sm", children: consultation.created_at ? new Date(consultation.created_at).toLocaleDateString() : '-' }), _jsx("td", { className: "px-3 py-2 text-sm", children: consultation.order_date ? new Date(consultation.order_date).toLocaleDateString() : '-' }), _jsx("td", { className: "px-3 py-2 text-sm", children: consultation.order_number || '-' }), _jsx("td", { className: "px-3 py-2 text-sm", children: consultation.mall_id || '-' }), _jsx("td", { className: "px-3 py-2 text-sm", children: consultation.recipient_name || '-' }), _jsx("td", { className: "px-3 py-2 text-sm", children: consultation.contact || '-' }), _jsx("td", { className: "px-3 py-2 text-sm max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap", children: (() => {
                                                        const productNames = getProductNames(consultation);
                                                        const productCount = getProductCount(consultation);
                                                        if (productNames.length > 0) {
                                                            if (productNames.length === 1) {
                                                                return productNames[0];
                                                            }
                                                            else {
                                                                return `${productNames[0]}외 ${productNames.length - 1}건`;
                                                            }
                                                        }
                                                        else {
                                                            return consultation.product_name || '-';
                                                        }
                                                    })() }), _jsx("td", { className: "px-3 py-2 text-sm", children: consultation.process_type || '-' }), _jsx("td", { className: "px-3 py-2 text-sm", children: consultation.receiver || '-' }), _jsx("td", { className: "px-3 py-2 text-sm", children: consultation.courier_company || consultation.send_courier_company || '-' }), _jsx("td", { className: "px-3 py-2 text-sm text-right", children: consultation.refund_amount ? consultation.refund_amount.toLocaleString() + '원' : '0원' }), _jsx("td", { className: "px-3 py-2 text-sm text-right", children: consultation.return_box ? consultation.return_box.toLocaleString() : '0' }), _jsx("td", { className: "px-3 py-2 text-sm text-right", children: consultation.courier_fee ? consultation.courier_fee.toLocaleString() + '원' : '0원' }), _jsx("td", { className: "px-3 py-2 text-sm text-right", children: consultation.send_box ? consultation.send_box.toLocaleString() : '0' }), _jsx("td", { className: "px-3 py-2 text-sm text-right", children: consultation.send_courier_fee ? consultation.send_courier_fee.toLocaleString() + '원' : '0원' }), _jsx("td", { className: "px-3 py-2 text-sm", children: _jsx("span", { className: `inline-block px-2 py-0.5 text-xs rounded ${consultation.status === '완료' ? 'bg-green-100 text-green-800' :
                                                            consultation.status === '전송완료' ? 'bg-green-100 text-green-800' :
                                                                consultation.status === '처리중' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`, children: consultation.status || '접수' }) }), _jsx("td", { className: "px-3 py-2 text-sm max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap", children: consultation.address || '-' }), _jsx("td", { className: "px-3 py-2 text-sm text-center sticky right-0 bg-white z-[5] border-l-2 border-gray-200 hover:bg-gray-50", children: _jsx("button", { className: "h-9 px-4 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700", onClick: () => {
                                                            consultation.consultationId = consultation.id;
                                                            if (consultation.products && consultation.products.length > 0) {
                                                            }
                                                            else {
                                                            }
                                                            // 현재 consultations 배열에서 같은 ID의 데이터 확인
                                                            const originalConsultation = consultations.find(c => c.id === consultation.id);
                                                            openClaimModal(consultation);
                                                        }, title: "\uD074\uB808\uC784\uC811\uC218", children: "\uD074\uB808\uC784\uC811\uC218" }) })] }, consultation.id)))) : (_jsx("tr", { children: _jsx("td", { colSpan: 20, className: "px-3 py-8 text-center text-sm text-gray-500", children: "\uAC80\uC0C9 \uACB0\uACFC\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4." }) })) })] }) }) }), filteredConsultations.length > 0 && (_jsx("div", { className: "mt-3", children: _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, itemsPerPage: itemsPerPage, totalItems: filteredConsultations.length, selectedItems: selectedConsultations, onPageChange: handlePageChange, onItemsPerPageChange: (num) => {
                                setItemsPerPage(num);
                                setCurrentPage(1);
                            } }) }))] }), _jsx(ImprovedClaimModal, { isOpen: showClaimModal, onClose: handleClaimClose, onSubmit: handleClaimSubmit, isLoading: isClaimLoading, orderData: claimOrder })] }));
};
export default ConsultationProcessing;
